import { useEffect, useRef, useState } from "react";
import { api, apiError, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Send, Users, Lock } from "lucide-react";
import { toast } from "sonner";

function useThread(eventId, joined) {
  const [data, setData] = useState({ messages: [], can_post: false, reason: null });
  const load = () => api.get(`/events/${eventId}/thread`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    let ws; let closed = false;
    api.get("/ws-token").then(({ data: t }) => {
      if (closed) return;
      ws = new WebSocket(`${base}/api/ws/event/${eventId}?token=${encodeURIComponent(t.token)}`);
      ws.onmessage = (e) => {
        try {
          const p = JSON.parse(e.data);
          if (p.type === "message") setData((d) => d.messages.some((m) => m.id === p.message.id) ? d : { ...d, messages: [...d.messages, p.message] });
        } catch {}
      };
    }).catch(() => {});
    const poll = setInterval(load, 5000);
    return () => { closed = true; clearInterval(poll); if (ws) ws.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, joined]);
  return [data, setData];
}

export function EventThread({ eventId, joined }) {
  const { user } = useAuth();
  const [data, setData] = useThread(eventId, joined);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [data.messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const val = text.trim();
    if (!val) return;
    setSending(true);
    try {
      const { data: msg } = await api.post(`/events/${eventId}/thread`, { text: val });
      setText("");
      setData((d) => d.messages.some((m) => m.id === msg.id) ? d : { ...d, messages: [...d.messages, msg] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <section data-testid="event-thread" className="glass rounded-2xl p-5 mt-8">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-5 w-5 text-amber-400" />
        <h2 className="font-display font-bold text-xl">Event group chat</h2>
        <span className="ml-auto text-xs text-slate-400">{data.messages.length} messages</span>
      </div>
      <div ref={scrollRef} className="max-h-80 overflow-y-auto no-scrollbar space-y-3 pr-1">
        {data.messages.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No messages yet — say hi to everyone going.</p>}
        {data.messages.map((m) => {
          const mine = m.user_id === user.user_id;
          return (
            <div key={m.id} data-testid="thread-message" className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`}>
              <img src={imgUrl(m.photo)} alt="" className="h-8 w-8 rounded-full object-cover shrink-0 bg-white/10" />
              <div className={`max-w-[78%] ${mine ? "text-right" : ""}`}>
                <p className="text-[11px] text-slate-400 flex items-center gap-1 justify-start">{mine ? "You" : m.name}{m.verified && <VerifiedBadge size="xs" />}</p>
                <div className={`inline-block px-3.5 py-2 text-sm rounded-2xl mt-0.5 ${mine ? "btn-gold rounded-tr-md" : "bg-white/5 border border-white/10 rounded-tl-md"}`}>{m.text}</div>
              </div>
            </div>
          );
        })}
      </div>
      {data.can_post ? (
        <form onSubmit={send} className="flex items-center gap-2 mt-4">
          <input data-testid="thread-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Message everyone going…"
            className="flex-1 h-11 rounded-full bg-white/5 border border-white/10 px-5 outline-none focus:border-amber-400/40 transition-colors text-sm" />
          <button data-testid="thread-send" type="submit" disabled={sending || !text.trim()} className="h-11 w-11 rounded-full btn-gold flex items-center justify-center disabled:opacity-50"><Send className="h-4 w-4" /></button>
        </form>
      ) : (
        <p data-testid="thread-locked" className="mt-4 text-sm text-slate-300 flex items-center gap-2 justify-center"><Lock className="h-3.5 w-3.5 text-amber-400" /> {data.reason}</p>
      )}
    </section>
  );
}
